// services/chatRatings.ts
//
// The thumbs on one answer (ORA-464; backend `chatRatingsController`).
//
// THE CONTRACT THIS CLIENT IS WRITTEN AGAINST — ora-be must match it, or bump
// it and tell this file:
//
//   POST /api/v5/chat/messages/:messageId/rating
//   body: { rating: 'up' | 'down' | null }
//
//   200 { messageId, rating: 'up' | 'down' | null, ratedAt: string | null }
//   400 { error: 'invalid_request' }
//   404 { error: 'not_found' }   — unknown message, or someone else's
//
// Notes on the shape, and why:
//
// - `messageId` is the SERVER's id for the assistant message, which is also
//   the key of its turn trace. A message this client streamed but the server
//   has not named yet cannot be rated, and the bar does not offer it.
// - `null` clears the rating. That is what a second tap on the highlighted
//   thumb sends, so there is one endpoint and one shape rather than a delete.
// - Rating is idempotent and free: the same value twice is the same row, and
//   nothing here spends a chat.
// - Never throws. The highlight is the student's own choice and stays put; a
//   failed write is not worth an error state on a message they have read, and
//   the next tap tries again. The boolean is there for the caller that wants
//   to know.

import axiosInstance from './axiosCreator';

/** The student's verdict on one answer; null means unrated (ORA-464). */
export type ChatMessageRating = 'up' | 'down' | null;

export const chatMessageRatingRoute = (messageId: string): string =>
  `/api/v5/chat/messages/${encodeURIComponent(messageId)}/rating`;

/**
 * Record (or clear) the student's rating of one answer. Resolves true when the
 * server accepted it.
 */
export const rateChatMessage = async (
  messageId: string,
  rating: ChatMessageRating
): Promise<boolean> => {
  try {
    await axiosInstance.post(chatMessageRatingRoute(messageId), { rating });
    return true;
  } catch {
    return false;
  }
};
